import React from "react";
import QrReader from "react-qr-reader";
import { Layout } from "../components/Layout/Layout";
import Button, { ButtonDesign, ButtonSize } from "../components/Button/Button";
import FieldSet from "../components/FieldSet/FieldSet";
import Input from "../components/Input/Input";
import { view } from "@orderofchaos/mvvm";
import { QRViewModel, type QRNavigableEntity } from "./QRViewModel";
import styles from "./QR.module.css";

interface QRProps {}

function entityLabel(entity: QRNavigableEntity): string {
  return entity.id.startsWith("prod_") ? "продукт" : "коробка";
}

export const QR: React.FC<QRProps> = view(QRViewModel)(({
  viewModel: {
    onError,
    onScan,
    results,
    hasResults,
    target,
    scanError,
    statusText,
    manualCode,
    isResolving,
    isScannerEnabled,
    facingMode,
    scannerDelay,
    onManualCodeChange,
    resolveManualCode,
    clearResult,
    toggleScanner,
    switchCamera,
    goToQrManagement,
    openTarget,
    openEntity,
  },
}) => {
  return (
    <Layout title="поиск по QR">
      <div className={styles.root}>
        <div className={styles.controls}>
          <Button
            design={
              isScannerEnabled ? ButtonDesign.secondary : ButtonDesign.basic
            }
            size={ButtonSize.medium}
            onClick={toggleScanner}
          >
            {isScannerEnabled ? "остановить сканер" : "включить сканер"}
          </Button>
          <Button
            design={ButtonDesign.basic}
            size={ButtonSize.medium}
            onClick={switchCamera}
            disabled={!isScannerEnabled}
          >
            камера: {facingMode === "environment" ? "задняя" : "фронтальная"}
          </Button>
          <Button
            design={ButtonDesign.ghost}
            size={ButtonSize.medium}
            onClick={clearResult}
          >
            очистить
          </Button>
          <Button
            design={ButtonDesign.basic}
            size={ButtonSize.medium}
            onClick={goToQrManagement}
          >
            печать QR
          </Button>
        </div>

        <div className={styles.scannerWrap}>
          {isScannerEnabled ? (
            <QrReader
              delay={scannerDelay}
              facingMode={facingMode}
              onError={onError}
              onScan={(value) => void onScan(value)}
              className={styles.reader}
              style={{ width: "100%" }}
            />
          ) : (
            <div className={styles.placeholder}>сканер остановлен</div>
          )}
          <div className={styles.overlayFrame} />
        </div>

        <p className={styles.status}>
          {isResolving ? "обрабатываем код..." : statusText}
        </p>
        {scanError && <p className={styles.error}>{scanError}</p>}

        <FieldSet title={"ввести код вручную"}>
          <div className={styles.manualRow}>
            <Input
              value={manualCode}
              onChange={onManualCodeChange}
              placeholder="prod_..., box_... или qr_..."
            />
            <Button
              design={ButtonDesign.primary}
              size={ButtonSize.medium}
              onClick={() => void resolveManualCode()}
              disabled={isResolving}
            >
              найти
            </Button>
          </div>
        </FieldSet>

        {!hasResults && (
          <p className={styles.empty}>путь к сущности пока не найден</p>
        )}

        {hasResults && (
          <div className={styles.results}>
            <div className={styles.resultsHeader}>
              <h3 className={styles.title}>путь к найденной сущности</h3>
              <Button
                design={ButtonDesign.primary}
                size={ButtonSize.medium}
                onClick={openTarget}
              >
                открыть цель
              </Button>
            </div>

            <ul className={styles.pathList}>
              {results.map((entity, index) => {
                const isTarget = target?.id === entity.id;
                return (
                  <li key={entity.id} className={styles.pathItem}>
                    <button
                      type="button"
                      className={
                        isTarget ? styles.pathButtonTarget : styles.pathButton
                      }
                      onClick={() => openEntity(entity)}
                    >
                      <span className={styles.pathIndex}>{index + 1}</span>
                      <span className={styles.pathText}>
                        <strong>{entity.name}</strong>
                        <small>
                          {entityLabel(entity)} · {entity.id}
                        </small>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
});
